/**
 * <LiveTandemBubble> — la bulle tandem du live : Sam et le masteur, l'un devant l'autre (organism).
 *
 * Elle remplace la vignette du masteur à son prénom (dans la bascule de l'en-tête) et Sam seul sur
 * la scène (Wajih, 19/10). Elle ne vit que là où la visio n'est pas à l'écran (l'activité au
 * téléphone) : ailleurs, le tandem se tient dans la visio elle-même (`<LiveStage tandem>`), pour que
 * le masteur ne se montre jamais deux fois. En bas à droite de l'écran, deux visages ronds de la même
 * taille, l'un posé devant, l'autre derrière lui, décalé, plus petit et estompé — sans rotation
 * (Wajih, 19/10 : la perspective donnait l'impression d'une image compressée). **Celui de devant se
 * tient en bas à gauche, celui de derrière en retrait en haut à droite** (Wajih, 22/10, comme dans la
 * visio) : ce que dit Sam, à gauche de la bulle, sort de celui qui est devant :
 *  - **en Concentré** (et pendant l'attente du masteur), Sam est devant : c'est lui qui accompagne
 *    l'exercice ; le masteur se tient derrière ;
 *  - **en Dialogue**, ils basculent : le masteur passe devant, Sam derrière.
 *
 * Qui mène arrive en props (`lead`), et le passage de l'un à l'autre est une **orbite** (Wajih,
 * 20/10) : les deux visages tournent autour de leur centre commun ; celui de derrière grandit, son
 * estompe s'en va, et il passe devant à mi-course ; celui de devant recule, rapetisse et s'estompe. À
 * la durée `entree` et à la courbe de la marque (`theme.motion`), par `useTandemOrbit` ; l'arrivée est
 * à plat. En mouvement réduit, rien ne bouge : ils sont aussitôt à leur place.
 *
 * **La visio sort d'elle et y rentre** (Wajih, daily du 21/10) : l'orbite se joue d'abord, puis la visio
 * grandit depuis son visage de devant, ou y revient en rétrécissant. La page mesure ce visage
 * (`frontRef`) et cache la bulle le temps que la visio la couvre (`hidden`) ; elle reste posée, et se
 * mesure.
 *
 * Le visage du masteur porte sa caméra (`tutorVideo`) ou sa photo, et le liseré du mode qu'il a
 * posé, vert en Dialogue, violet en Concentré, comme la mini-visio qu'il remplace. **En Concentré**
 * (Wajih, 20/10), il reste rond et ne rapetisse plus derrière Sam : il garde la taille de Sam, à peine
 * estompé, et porte la marque du mode, le son coupé sur une pastille violette (le Concentré se voit là où
 * est la visio). Sam, lui, rapetisse toujours derrière le masteur en Dialogue. Touché, il ouvre
 * la visio (`onPressTutor`, au téléphone, depuis l'activité). Celui de Sam ouvre sa conversation
 * (`sam.onOpen`) quand il est devant ; derrière, il se tait, comme Sam s'effaçait en Dialogue. Ce
 * que Sam vient de dire (`sam.peek`) se pose à gauche de la bulle, quand il est devant.
 *
 * Sans `sam` (une salle sans checkpoint), le masteur est seul dans la bulle.
 *
 * Elle ne se place pas elle-même : la page la pose au coin de l'activité ou de la scène, à la
 * hauteur que réserve une tuile de Sam (`SAM_TILE_SIZE`).
 *
 * Composes DS pieces only — `<SamTile>`, `<SamPeek>`, `<VideoTile size="round">`, `<FocusRing>`.
 */
import React, { useMemo } from "react"
import { Animated, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { FocusRing, focusRingHost } from "../../atoms/FocusRing"
import { Icon } from "../../atoms/icons/Icon"
import { useFadeIn } from "../../hooks/useFadeIn"
import { type TandemOrbitFaceStyle, type TandemOrbitGeometry, useTandemOrbit } from "../../hooks/useTandemOrbit"
import { SamPeek } from "../../molecules/SamPeek"
import { SAM_TILE_SIZE, SamTile } from "../../molecules/SamTile"
import { type UISoundMode } from "../../molecules/SoundModeBadge"
import { type UILiveParticipant, VideoTile } from "../../molecules/VideoTile"
import { samPeekText } from "../../utils/samPeek"
import {
    TANDEM_BACK_SHIFT_Y,
    TANDEM_BOX_WIDTH,
    TANDEM_FACE,
    TANDEM_SAM_BACK_SHIFT_X,
    TANDEM_TUTOR_BACK_SCALE,
    TANDEM_TUTOR_BACK_SHIFT_X,
} from "./tandemBubbleGeometry"

/** Qui est devant. */
export type UITandemLead = "sam" | "tutor"

export type LiveTandemBubbleProps = {
    /** Qui est devant : Sam en Concentré et pendant l'attente, le masteur en Dialogue. */
    lead: UITandemLead
    /** Le mode posé par le masteur : le liseré de son visage. */
    soundMode: UISoundMode
    tutor: UILiveParticipant
    /** Son prénom, pour le lecteur d'écran. */
    tutorName: string
    /** La vraie piste de sa caméra dans son visage. Absente : sa photo. */
    tutorVideo?: React.ReactNode
    /** Le masteur n'est pas encore là : son visage estompé. Défaut `false`. */
    tutorAbsent?: boolean
    /** Son visage touché : la visio. Absent : inerte. */
    onPressTutor?: () => void
    /** Sam. Absent : le masteur seul dans la bulle. */
    sam?: {
        onOpen: () => void
        /** Ce que Sam vient de dire, à gauche de la bulle, quand il est devant. */
        peek?: string | null
        onDismissPeek?: () => void
    }
    /** La place du visage de devant (sans rien dedans) : la page y mesure d'où la visio sort. */
    frontRef?: React.Ref<View>
    /** Posée mais invisible et inerte : la visio la couvre, ou va la couvrir. Défaut `false`. */
    hidden?: boolean
    testID?: string
}

const FACE = TANDEM_FACE
/** Le visage de derrière : plus petit, en retrait vers le haut et la droite (`tandemBubbleGeometry`), estompé. */
const BACK_SCALE = 0.72
const BACK_SHIFT_Y = TANDEM_BACK_SHIFT_Y
const BACK_OPACITY = 0.5
/**
 * Le masteur derrière Sam (le Concentré, l'attente) : à la taille de Sam (`TANDEM_TUTOR_BACK_SCALE`), plus
 * décalé pour rester visible, et à peine estompé (sa marque reste lisible).
 */
const TUTOR_BACK_SCALE = TANDEM_TUTOR_BACK_SCALE
const TUTOR_BACK_OPACITY = 0.9
/** La marque du Concentré : une pastille violette, le son coupé, à un coin bas du visage du masteur. */
const MARK = 22
const BOX_HEIGHT = SAM_TILE_SIZE
/**
 * L'orbite de la bulle : les deux visages échangent la place de devant et celle de derrière, autour de
 * leur milieu. À mi-course, celui qui recule passe à droite, vers le retrait, celui qui avance à gauche,
 * à hauteur du milieu : la bulle ne déborde ni sur la barre de copie ni sur le bord de l'écran, et les
 * deux visages se croisent sans se recouvrir (48 px chacun à mi-course, leurs centres à 49 px) — celui
 * qui avance passe devant sans que rien ne saute.
 */
const SLOTS = {
    front: { center: { x: 0, y: 0 }, scale: 1 },
    back: { center: { x: TANDEM_SAM_BACK_SHIFT_X, y: BACK_SHIFT_Y }, scale: BACK_SCALE },
}
const TUTOR_SLOTS = {
    front: SLOTS.front,
    back: { center: { x: TANDEM_TUTOR_BACK_SHIFT_X, y: BACK_SHIFT_Y }, scale: TUTOR_BACK_SCALE },
}
const ORBIT: TandemOrbitGeometry = { sam: SLOTS, other: TUTOR_SLOTS, swing: { x: 24, y: 4 } }
/** D'un centre de visage à son bord, à 45° : la marque s'y pose, à cheval. */
const MARK_REACH = (FACE / 2) * Math.SQRT1_2
/**
 * Le centre de la marque du Concentré, dans la boîte : derrière Sam, au bas droit du visage du masteur,
 * loin de Sam ; sans Sam, le masteur devant, au bas gauche du sien.
 */
const MARK_BEHIND_SAM = {
    x: FACE / 2 + TANDEM_TUTOR_BACK_SHIFT_X + MARK_REACH,
    y: BOX_HEIGHT - FACE / 2 + BACK_SHIFT_Y + MARK_REACH,
}
const MARK_ALONE = { x: FACE / 2 - MARK_REACH, y: BOX_HEIGHT - FACE / 2 + MARK_REACH }
/** Le liseré du mode, autour du visage du masteur. */
const RING = 2

const TEXTS = {
    tutor: (name: string, mode: UISoundMode) =>
        `${name !== "" ? name : "Ton masteur"}, ${mode === "focused" ? "en Concentré" : "en Dialogue"}`,
}

/** `<Pressable>` ne type que `pressed` ; react-native-web ajoute `focused` à l'exécution. */
type PressableVisualState = { pressed: boolean; focused?: boolean }

/**
 * L'estompe d'un visage sur l'orbite, gardée d'un rendu à l'autre : une interpolation neuve à chaque
 * rendu casse le mémo des props animées (RN 0.81), qui se rattachent au graphe natif à chaque
 * `ActiveSpeakersChanged` — un saut possible en pleine orbite.
 */
function useFade(face: TandemOrbitFaceStyle | null, backOpacity = BACK_OPACITY) {
    return useMemo(
        () => (face != null ? face.back.interpolate({ inputRange: [0, 1], outputRange: [1, backOpacity] }) : null),
        [face, backOpacity]
    )
}

/** La place d'un visage sur l'orbite : derrière, il s'estompe ; devant, il se peint par-dessus. */
function onOrbit(
    face: TandemOrbitFaceStyle | null,
    fade: Animated.AnimatedInterpolation<number> | null,
    inFront: boolean
) {
    return {
        zIndex: inFront ? 1 : 0,
        ...(face != null && fade != null && { opacity: fade, transform: face.transform }),
    }
}

export function LiveTandemBubble(props: LiveTandemBubbleProps) {
    const {
        lead,
        soundMode,
        tutor,
        tutorName,
        tutorVideo,
        tutorAbsent = false,
        onPressTutor,
        sam,
        frontRef,
        hidden = false,
        testID,
    } = props

    // Sans Sam, le masteur mène toujours.
    const samLeads = sam != null && lead === "sam"
    const orbit = useTandemOrbit({ samLeads, geometry: ORBIT })
    const tutorFade = useFade(orbit.other, TUTOR_BACK_OPACITY)
    const samFade = useFade(orbit.sam)
    // La marque du Concentré paraît quand le masteur est passé derrière Sam, et part quand il revient.
    const tutorBack = orbit.other?.back
    const markOpacity = useMemo(
        () => tutorBack?.interpolate({ inputRange: [0, 0.5, 1], outputRange: [0, 0, 1] }) ?? null,
        [tutorBack]
    )
    const focused = soundMode === "focused"

    const tutorFace = (
        <Animated.View
            style={[styles.face, onOrbit(orbit.other, tutorFade, !orbit.samInFront || sam == null)]}
            pointerEvents={onPressTutor != null ? "auto" : "none"}
        >
            <Pressable
                onPress={onPressTutor}
                disabled={onPressTutor == null}
                accessibilityRole={onPressTutor != null ? "button" : undefined}
                accessibilityLabel={TEXTS.tutor(tutorName, soundMode)}
                style={({ pressed }: PressableVisualState) => [focusRingHost, pressed && styles.pressed]}
            >
                {({ focused }: PressableVisualState) => (
                    <>
                        <FocusRing visible={focused === true} radius={FACE / 2} />
                        <View style={styles.ring(soundMode)}>
                            <VideoTile
                                participant={tutor}
                                size="round"
                                diameter={FACE - 2 * RING}
                                video={orbit.showVideo ? tutorVideo : undefined}
                                absent={tutorAbsent}
                            />
                        </View>
                    </>
                )}
            </Pressable>
        </Animated.View>
    )

    // Ce que dit Sam sort de lui une fois l'orbite posée, en fondu, comme sur la visio.
    const peekText = samLeads && orbit.landed ? samPeekText(sam?.peek) : ""
    const peekIn = useFadeIn(peekText !== "")
    const samFace = sam != null && (
        // Derrière, Sam se tait aussi pour le lecteur d'écran et le clavier : ni focalisable, ni activable.
        <Animated.View
            style={[styles.face, onOrbit(orbit.sam, samFade, orbit.samInFront)]}
            pointerEvents={samLeads ? "auto" : "none"}
            aria-hidden={!samLeads}
            importantForAccessibility={samLeads ? "auto" : "no-hide-descendants"}
        >
            <SamTile onPress={sam.onOpen} size={FACE} unread={peekText !== ""} disabled={!samLeads} />
        </Animated.View>
    )

    return (
        // `box-none` : entre l'aperçu et la bulle, ce qui est derrière reste touchable.
        <View
            style={[styles.root, hidden && styles.hidden]}
            pointerEvents={hidden ? "none" : "box-none"}
            aria-hidden={hidden}
            importantForAccessibility={hidden ? "no-hide-descendants" : "auto"}
            testID={testID}
        >
            {peekText !== "" && sam != null && (
                <Animated.View style={[styles.peek, { opacity: peekIn }]}>
                    <SamPeek text={peekText} onPress={sam.onOpen} onDismiss={sam.onDismissPeek} />
                </Animated.View>
            )}
            <View style={[styles.box, sam == null && styles.boxAlone]} pointerEvents="box-none">
                <View ref={frontRef} style={styles.face} pointerEvents="none" collapsable={false} />
                {/* Celui de devant se peint par-dessus (`zIndex`) : l'échange se fait à mi-course. */}
                {tutorFace}
                {samFace}
                {focused && (
                    // Sans Sam, le masteur reste devant : la marque se pose sur son visage, sans attendre.
                    <Animated.View
                        style={[
                            styles.mark,
                            sam != null ? styles.markBehindSam : styles.markAlone,
                            sam != null && markOpacity != null && { opacity: markOpacity },
                        ]}
                        pointerEvents="none"
                    >
                        <Icon name="volume-mute" size={12} color="white" />
                    </Animated.View>
                )}
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        gap: theme.spacing["2"],
    },
    /** La boîte : le visage de devant, plus le recul de celui de derrière ; le masteur seul, son visage. */
    box: { width: TANDEM_BOX_WIDTH, height: BOX_HEIGHT },
    boxAlone: { width: FACE },
    hidden: { opacity: 0 },
    /** Le visage de devant, en bas à gauche de la boîte ; l'orbite pose celui de derrière en haut à droite. */
    face: {
        position: "absolute",
        left: 0,
        bottom: 0,
        width: FACE,
        height: FACE,
    },
    ring: (mode: UISoundMode) => ({
        width: FACE,
        height: FACE,
        borderRadius: FACE / 2,
        borderWidth: RING,
        borderColor: theme.colors[mode === "focused" ? "purple" : "success"],
        backgroundColor: theme.colors.white,
    }),
    /** L'aperçu se pose un peu au-dessus du pied de la bulle : sa queue vise la tête de Sam. */
    peek: {
        flexShrink: 1,
        marginBottom: theme.spacing["3"],
    },
    /** Le retour de pression d'un bouton d'icône (`<IconButton>`). */
    pressed: { transform: [{ scale: 0.98 }] },
    /** La marque du Concentré, à cheval sur un coin bas du visage du masteur, devant Sam et lui. */
    mark: {
        position: "absolute",
        zIndex: 2,
        width: MARK,
        height: MARK,
        borderRadius: MARK / 2,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.purple,
        borderWidth: 2,
        borderColor: theme.colors.white,
    },
    markBehindSam: { left: MARK_BEHIND_SAM.x - MARK / 2, top: MARK_BEHIND_SAM.y - MARK / 2 },
    markAlone: { left: MARK_ALONE.x - MARK / 2, top: MARK_ALONE.y - MARK / 2 },
}))
